import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  School,
  Users,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  TrendingUp,
  AlertCircle,
  Search,
  Plus,
  DollarSign,
  CheckCircle,
  Filter
} from 'lucide-react'
import supabase from '../../services/supabaseClient'
import LanguageSelector from '../../components/LanguageSelector'

// Sub-pages
import SchoolsList from './SchoolsList'
import SchoolDetails from './SchoolDetails'
import PlatformAnalytics from './PlatformAnalytics'

export default function SuperAdminDashboard() {
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeSchools: 0,
    totalUsers: 0,
    trialSchools: 0
  })

  useEffect(() => {
    fetchPlatformStats()
  }, [])

  async function fetchPlatformStats() {
    try {
      const { count: schoolsCount } = await supabase
        .from('schools')
        .select('*', { count: 'exact', head: true })

      const { count: activeCount } = await supabase
        .from('schools')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)

      const { count: trialCount } = await supabase
        .from('schools')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'trial')

      const { count: usersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })

      setStats({
        totalSchools: schoolsCount || 0,
        activeSchools: activeCount || 0,
        totalUsers: usersCount || 0,
        trialSchools: trialCount || 0
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const navigation = [
    { name: 'Dashboard', href: '/admin/super', icon: LayoutDashboard },
    { name: 'All Schools', href: '/admin/super/schools', icon: School },
    { name: 'Platform Analytics', href: '/admin/super/analytics', icon: TrendingUp },
    { name: 'Settings', href: '/admin/super/settings', icon: Settings },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white border-r border-gray-100">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center shadow-glow">
                <School className="h-5 w-5 text-white" />
              </div>
              <span className="ml-3 text-lg font-bold text-text-primary">Super Admin</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-100">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center shadow-glow">
              <School className="h-5 w-5 text-white" />
            </div>
            <span className="ml-3 text-lg font-bold text-text-primary">Super Admin</span>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="dashboard-header sticky top-0 z-30">
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search schools, users..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-text-primary placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
            
            <div className="flex items-center ml-auto space-x-4">
              <LanguageSelector size="small" />
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-2 right-2 h-2.5 w-2.5 bg-primary-500 rounded-full border-2 border-white"></span>
              </button>
              <div className="flex items-center pl-4 border-l border-gray-100">
                <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-semibold shadow-glow">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className="text-sm font-semibold text-text-primary">{profile?.first_name} {profile?.last_name}</p>
                  <p className="text-xs text-text-secondary">Super Administrator</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/"
              element={
                <div className="space-y-8 animate-fade-in">
                  {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-text-primary font-heading">Platform Overview</h1>
                      <p className="mt-1 text-sm text-text-secondary">Monitor your platform's performance and growth</p>
                    </div>
                    <button className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Add School
                    </button>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon primary">
                          <School className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">Total Schools</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalSchools}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center text-sm">
                        <span className="text-success-500 font-medium">+12%</span>
                        <span className="text-text-secondary ml-2">from last month</span>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon success">
                          <Users className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">Total Users</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalUsers}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center text-sm">
                        <span className="text-success-500 font-medium">+8%</span>
                        <span className="text-text-secondary ml-2">from last month</span>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon warning">
                          <AlertCircle className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">Trial Schools</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.trialSchools}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center text-sm">
                        <span className="text-orange-500 font-medium">{stats.trialSchools} active</span>
                        <span className="text-text-secondary ml-2">trials running</span>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon secondary">
                          <DollarSign className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">Active Schools</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.activeSchools}</p>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center text-sm">
                        <span className="text-success-500 font-medium">{Math.round((stats.activeSchools / (stats.totalSchools || 1)) * 100)}%</span>
                        <span className="text-text-secondary ml-2">activation rate</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="card p-6">
                    <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">Quick Actions</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Link
                        to="/admin/super/schools"
                        className="quick-action group"
                      >
                        <div className="p-3 rounded-xl bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                          <School className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-text-primary">View All Schools</p>
                          <p className="text-sm text-text-secondary">Manage registered schools</p>
                        </div>
                      </Link>
                      <Link
                        to="/admin/super/analytics"
                        className="quick-action group"
                      >
                        <div className="p-3 rounded-xl bg-card-green text-success-500 group-hover:bg-success-100 transition-colors">
                          <TrendingUp className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-text-primary">Platform Analytics</p>
                          <p className="text-sm text-text-secondary">View detailed reports</p>
                        </div>
                      </Link>
                      <div className="quick-action group cursor-pointer">
                        <div className="p-3 rounded-xl bg-card-blue text-blue-500 group-hover:bg-blue-100 transition-colors">
                          <Settings className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="font-semibold text-text-primary">System Settings</p>
                          <p className="text-sm text-text-secondary">Configure platform</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity & System Status */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-bold text-text-primary font-heading">Recent Schools</h2>
                        <Link to="/admin/super/schools" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                          View all
                        </Link>
                      </div>
                      <div className="space-y-4">
                        {[1, 2, 3].map((_, i) => (
                          <div key={i} className="flex items-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="h-12 w-12 rounded-xl bg-card-teal flex items-center justify-center text-primary-600 font-bold">
                              S{i + 1}
                            </div>
                            <div className="ml-4 flex-1">
                              <p className="font-medium text-text-primary">School Name {i + 1}</p>
                              <p className="text-sm text-text-secondary">Joined 2 days ago</p>
                            </div>
                            <span className="badge badge-success">Active</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="card p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-bold text-text-primary font-heading">System Status</h2>
                        <span className="badge badge-success flex items-center">
                          <span className="w-1.5 h-1.5 bg-success-500 rounded-full mr-1.5 animate-pulse"></span>
                          Operational
                        </span>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-success-500 mr-3"></div>
                            <span className="text-text-secondary">Database</span>
                          </div>
                          <span className="text-sm text-text-secondary">99.9% uptime</span>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-success-500 mr-3"></div>
                            <span className="text-text-secondary">API</span>
                          </div>
                          <span className="text-sm text-text-secondary">&lt; 100ms latency</span>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-success-500 mr-3"></div>
                            <span className="text-text-secondary">Storage</span>
                          </div>
                          <span className="text-sm text-text-secondary">24% used</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="/schools" element={<SchoolsList />} />
            <Route path="/schools/:id" element={<SchoolDetails />} />
            <Route path="/analytics" element={<PlatformAnalytics />} />
            <Route path="*" element={<Navigate to="/admin/super" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
